import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import {
    TableRow,
    TableCell,
    TableHeader,
    TableColumn,
    MobileCardRow,
    MobileCardWrapper,
    TableBody,
} from './dashboard/table-components';
import './../scss/components/invoice-table-detailed.style.scss';
import { ArrowLeftIcon, ArrowRightIcon } from '../assets/dashboard-icon.svg';
import { formatDate } from '../helper/common.helper';
import { calculatePaginationPages } from '../helper/pagination.helper';
import { useDeductions } from '../hooks/useDeductions';
import CardSkeleton from './SkeletonLoading';

interface DeductionInvoiceSectionProps {
    selectedYear?: string;
    selectedMonth?: string;
    selectedClient?: string;
    searchQuery?: string;
}

export const DeductionINvoiceSection: React.FC<DeductionInvoiceSectionProps> = ({
    selectedYear,
    selectedMonth,
    selectedClient,
    searchQuery
}) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Prepare filters object with useMemo to prevent unnecessary re-renders
    const filters = useMemo(() => ({
        year: selectedYear,
        month: selectedMonth,
        searchQuery: searchQuery || '',
        userId: selectedClient
    }), [selectedYear, selectedMonth, selectedClient, searchQuery]);

    const {
        deductions,
        loading,
        error,
        currentPage,
        totalPages,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
    } = useDeductions(10, 2, filters);

    return (
        <div className={`invoice-table-container`}>
            <div className="invoice-table-content">


                {/* Table */}
                {deductions && (Array.isArray(deductions) && deductions.length >= 0) && (
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                {[t('SERIAL_NUMBER'), t('INVOICE_NUMBER'), t('CUSTOMER_NAME_LABEL'), t('DATE_LABEL'), t('TOTAL_AMOUNT'), t('DEDUCTION_AMOUNT'), t('VAT'), t('ARTICLE_UNIT'), t('WORKING_DATE')].map((header, index) => (
                                    <TableColumn key={index}>{header}</TableColumn>
                                ))}
                            </TableHeader>
                        )}

                        <TableBody>
                            {loading && (
                                <CardSkeleton/>
                            )}

                            {/* No drafts found message */}
                            {Array.isArray(deductions) && deductions.length === 0 && (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    {t('NO_DATA_FOUND')}
                                </div>
                            )}
                            {isMobile ? (
                                // Mobile: Card-based layout
                                (Array.isArray(deductions) ? deductions : [])?.map((deduction: any, index: number) => (
                                    <MobileCardWrapper key={deduction.id}>
                                        <MobileCardRow label={t('SERIAL_NUMBER')}>
                                            <span className="invoice-table-detailed__invoice-link">{(currentPage - 1) * 10 + index + 1}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NUMBER')}>
                                            {deduction?.invoiceNumber}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CUSTOMER_NAME_LABEL')}>
                                            {deduction?.customerName}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('DATE_LABEL')}>
                                            {formatDate(deduction.invoice.createdDate)}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('TOTAL_AMOUNT')}>
                                            {deduction.invoice.amount} {deduction.invoice.currency}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('DEDUCTION_AMOUNT')}>
                                            {deduction.amountPerUnit} {deduction.invoice.currency}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('VAT')}>
                                            {deduction.invoice.vat}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ARTICLE_UNIT')}>
                                            {deduction.amountPerUnit}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('WORKING_DATE')}>
                                            {formatDate(deduction.workingDate)}
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                (deductions as any[]).map((deduction: any, index: number) => (
                                    <TableRow key={deduction.id}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{(currentPage - 1) * 10 + index + 1}</span>
                                        </TableCell>
                                        <TableCell>{deduction?.invoiceNumber}</TableCell>
                                        <TableCell>{deduction.customerName}</TableCell>
                                        <TableCell>{formatDate(deduction.invoice.createdDate)}</TableCell>
                                        <TableCell>
                                            {deduction.invoice.amount} {deduction.invoice.currency}
                                        </TableCell>
                                        <TableCell>{deduction.amountPerUnit} {deduction.invoice.currency}</TableCell>
                                        <TableCell>{deduction.invoice.vat}</TableCell>
                                        <TableCell>{deduction.unit}</TableCell>
                                        <TableCell>{formatDate(deduction.workingDate)}</TableCell>


                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={() => goToPreviousPage()}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP')}</span>
                            </button>

                            {calculatePaginationPages(currentPage, totalPages).map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={() => goToNextPage()}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


